import React from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Chip } from 'src/components/shared/Chip'
import { InsurancePolicyProductActionsCell } from 'src/components/insurance-policy/grid/InsurancePolicyProductActionsCell'
import { InsurancePolicyProductContractCell } from 'src/components/insurance-policy/grid/InsurancePolicyProductContractCell'
import type { InsurancePolicyProduct } from 'src/interfaces/insurancePolicy'
import { formatCentsAsUsd, formatRate } from 'src/utils/invoiceMoney'
import { insurancePolicyProductTableHeaderTitles } from './constants'
import { InsurancePolicyProductsTableFields } from './enums'

const MISSING_VALUE = '—'

/**
 * Wiring only — money formatting comes from utils/invoiceMoney, the contract title from
 * InsurancePolicyProductContractCell, actions from InsurancePolicyProductActionsCell.
 *
 * Same factory rationale as `buildInsurancePolicyColumns`: it closes over two stable handlers
 * and nothing else, and callers must memoize the result.
 */
export const buildInsurancePolicyProductColumns = (
  onEditPolicyProduct: (policyProduct: InsurancePolicyProduct) => void,
  onDeletePolicyProduct: (policyProduct: InsurancePolicyProduct) => void,
  // `insurance_policies_manage` (ENG-1207 part 2). Edit and Remove both need it, so without it
  // the actions column is dropped rather than left as an empty menu.
  canManage: boolean,
): Array<GridColDef<InsurancePolicyProduct>> => {
  const columns: Array<GridColDef<InsurancePolicyProduct>> = [
    {
      field: InsurancePolicyProductsTableFields.product,
      headerName:
        insurancePolicyProductTableHeaderTitles[InsurancePolicyProductsTableFields.product],
      flex: 2,
      minWidth: 180,
      sortable: true,
      hideable: false,
      display: 'flex',
      // The nested catalog row is what carries the name; sorting has to read the same value the
      // cell shows, or the column sorts by something invisible.
      valueGetter: (value: unknown, row: InsurancePolicyProduct) => {
        return row.insuranceProduct?.name ?? row.insuranceProductId
      },
      renderCell: (params: GridRenderCellParams<InsurancePolicyProduct>) => {
        const label = params.row.insuranceProduct?.name ?? params.row.insuranceProductId

        return (
          <Typography variant="body2" css={tw`truncate`} title={label}>
            {label}
          </Typography>
        )
      },
    },
    {
      field: InsurancePolicyProductsTableFields.allowOptOut,
      headerName:
        insurancePolicyProductTableHeaderTitles[InsurancePolicyProductsTableFields.allowOptOut],
      width: 100,
      sortable: true,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<InsurancePolicyProduct>) => {
        return (
          <Typography variant="body2">
            {params.row.allowOptOut ? 'Allowed' : 'Not allowed'}
          </Typography>
        )
      },
    },
    {
      field: InsurancePolicyProductsTableFields.feeCents,
      headerName:
        insurancePolicyProductTableHeaderTitles[InsurancePolicyProductsTableFields.feeCents],
      width: 120,
      sortable: true,
      align: 'right',
      headerAlign: 'right',
      // Stored in CENTS. `formatCentsAsUsd` renders an em dash for null rather than $0.00 — a
      // zero fee is a real, different reading from no fee recorded.
      valueFormatter: (value: unknown) => {
        return formatCentsAsUsd(value as number | null)
      },
    },
    {
      field: InsurancePolicyProductsTableFields.feePercent,
      headerName:
        insurancePolicyProductTableHeaderTitles[InsurancePolicyProductsTableFields.feePercent],
      width: 100,
      sortable: true,
      align: 'right',
      headerAlign: 'right',
      // A decimal percent — `2.5` means 2.5%. `formatRate` never divides by 100.
      valueFormatter: (value: unknown) => {
        return formatRate(value as number | null)
      },
    },
    {
      field: InsurancePolicyProductsTableFields.feeCapCents,
      headerName:
        insurancePolicyProductTableHeaderTitles[InsurancePolicyProductsTableFields.feeCapCents],
      width: 120,
      sortable: true,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value: unknown) => {
        return formatCentsAsUsd(value as number | null)
      },
    },
    {
      field: InsurancePolicyProductsTableFields.feeFrequency,
      headerName:
        insurancePolicyProductTableHeaderTitles[InsurancePolicyProductsTableFields.feeFrequency],
      width: 140,
      sortable: true,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<InsurancePolicyProduct>) => {
        // The RAW stored value. para-node's `PaymentPeriodType` has a sixth member, `custom`,
        // that the FE enum omits and the select never offers — but a legacy row carrying it
        // must still display, and `feeFrequency` is immutable so no form can correct it.
        return <Chip text={params.row.feeFrequency} />
      },
    },
    {
      field: InsurancePolicyProductsTableFields.contract,
      headerName:
        insurancePolicyProductTableHeaderTitles[InsurancePolicyProductsTableFields.contract],
      flex: 2,
      minWidth: 180,
      // The displayed title comes from another slice, so there is nothing on the row to sort or
      // export by; the raw id would sort by something the admin cannot see.
      sortable: false,
      disableExport: true,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<InsurancePolicyProduct>) => {
        return (
          <InsurancePolicyProductContractCell
            insuranceDocusignTemplateId={params.row.insuranceDocusignTemplateId}
          />
        )
      },
    },
    {
      field: InsurancePolicyProductsTableFields.description,
      headerName:
        insurancePolicyProductTableHeaderTitles[InsurancePolicyProductsTableFields.description],
      flex: 3,
      minWidth: 200,
      sortable: true,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<InsurancePolicyProduct>) => {
        const description = params.row.description?.trim()

        if (!description) {
          return (
            <Typography variant="body2" css={tw`text-shades-500`}>
              {MISSING_VALUE}
            </Typography>
          )
        }

        return (
          <Typography variant="body2" css={tw`truncate`} title={description}>
            {description}
          </Typography>
        )
      },
    },
    {
      field: InsurancePolicyProductsTableFields.actions,
      headerName:
        insurancePolicyProductTableHeaderTitles[InsurancePolicyProductsTableFields.actions],
      width: 68,
      sortable: false,
      hideable: false,
      disableExport: true,
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      renderCell: (params: GridRenderCellParams<InsurancePolicyProduct>) => {
        return (
          <InsurancePolicyProductActionsCell
            policyProduct={params.row}
            onEdit={onEditPolicyProduct}
            onDelete={onDeletePolicyProduct}
          />
        )
      },
    },
  ]

  if (canManage) {
    return columns
  }

  return columns.filter((column) => {
    return column.field !== InsurancePolicyProductsTableFields.actions
  })
}
